// src/pages/DispatchOverview.js — Admin portal dispatch overview
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { get, post } from '../utils/api';
import { Spinner, Alert, Modal, formatDate, formatCurrency, formatDuration } from '../components/Shared';
import { useAutoRefresh } from '../hooks/useAutoRefresh';

// toISOString() is always UTC — adjust by the browser's own offset first so
// "Today" always means the actual local calendar date.
function localDateString(d) {
  const offsetMs = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - offsetMs).toISOString().split('T')[0];
}
const today = () => localDateString(new Date());

export default function DispatchOverview() {
  const navigate = useNavigate();
  const [view,          setView]          = useState('classification'); // 'classification' | 'rounds'
  const [unclassified, setUnclassified] = useState([]);
  const [instation,    setInstation]    = useState([]);
  const [outstation,   setOutstation]   = useState([]);
  const [rounds,       setRounds]       = useState([]);
  const [loadingRounds, setLoadingRounds] = useState(false);
  const [expandedRound, setExpandedRound] = useState(null);
  const [roundInvoices, setRoundInvoices] = useState({}); // round.id -> invoices[]
  const [loadingInvoices, setLoadingInvoices] = useState(null); // round.id currently loading
  const [roundSortCol, setRoundSortCol] = useState('started_at');
  const [roundSortOrder, setRoundSortOrder] = useState('desc');
  const [invSortCol, setInvSortCol] = useState('invoice_number');
  const [invSortOrder, setInvSortOrder] = useState('asc');
  const [collapsedDateGroups, setCollapsedDateGroups] = useState(new Set());
  const [loading,      setLoading]      = useState(true);
  const [msg,          setMsg]          = useState(null);
  const [overrideInv,  setOverrideInv]  = useState(null);
  const [overrideForm, setOverrideForm] = useState({ delivery_type:'instation', reason:'' });
  const [saving,       setSaving]       = useState(false);
  const [tab,          setTab]          = useState('unclassified');
  const [dateFrom,     setDateFrom]     = useState(today());
  const [dateTo,       setDateTo]       = useState(today());
  const [allTime,      setAllTime]      = useState(false);

  const load = useCallback(async () => {
    try {
      const [unRes, inRes, outRes] = await Promise.all([
        get('/api/dispatch/unclassified'),
        get('/api/dispatch/instation'),
        get('/api/dispatch/outstation'),
      ]);
      setUnclassified(unRes.invoices  || []);
      setInstation(inRes.invoices     || []);
      setOutstation(outRes.invoices   || []);
    } catch (err) {
      setMsg({ type:'error', text: err.message });
    } finally { setLoading(false); }
  }, []);

  const lastUpdated = useAutoRefresh(load);

  const loadRounds = useCallback(async () => {
    setLoadingRounds(true);
    try {
      const params = new URLSearchParams();
      if (!allTime) {
        if (dateFrom) params.set('date_from', dateFrom);
        if (dateTo)   params.set('date_to', dateTo);
      }
      const res = await get(`/api/dispatch/rounds?${params.toString()}`);
      setRounds(res.rounds || []);
    } catch (err) {
      setMsg({ type:'error', text: err.message });
    } finally { setLoadingRounds(false); }
  }, [dateFrom, dateTo, allTime]);

  useEffect(() => {
    if (view === 'rounds') loadRounds();
  }, [view, dateFrom, dateTo, allTime]);

  async function toggleRound(round) {
    if (expandedRound === round.id) { setExpandedRound(null); return; }
    setExpandedRound(round.id);
    if (!roundInvoices[round.id]) {
      setLoadingInvoices(round.id);
      try {
        const res = await get(`/api/dispatch/rounds/${round.id}/invoices`);
        setRoundInvoices(prev => ({ ...prev, [round.id]: res.invoices || [] }));
      } catch (err) {
        setMsg({ type:'error', text: err.message });
      } finally { setLoadingInvoices(null); }
    }
  }

  async function submitOverride() {
    if (!overrideForm.reason) { setMsg({ type:'warning', text:'Please enter a reason for the override' }); return; }
    setSaving(true);
    try {
      await post(`/api/dispatch/classify-override/${overrideInv.invoice_id}`, overrideForm);
      setMsg({ type:'success', text:`Invoice reclassified as ${overrideForm.delivery_type}` });
      setOverrideInv(null);
      load();
    } catch (err) {
      setMsg({ type:'error', text: err.message });
    } finally { setSaving(false); }
  }

  const inDateRange = (inv) => {
    if (allTime) return true;
    const d = inv.received_at ? inv.received_at.split('T')[0] : null;
    if (!d) return true;
    if (dateFrom && d < dateFrom) return false;
    if (dateTo && d > dateTo) return false;
    return true;
  };

  const unclassifiedFiltered = unclassified.filter(inDateRange);
  const instationFiltered    = instation.filter(inDateRange);
  const outstationFiltered   = outstation.filter(inDateRange);

  // NOTE: tab counts' colors are the delivery-classification color-code
  // (unclassified/instation/outstation) — same categorical reasoning as
  // invoice stage colors, left untouched.
  const tabs = [
    { key:'unclassified', label:'Unclassified', count: unclassifiedFiltered.length, color:'bg-amber-500' },
    { key:'instation',    label:'Instation',    count: instationFiltered.length,    color:'bg-blue-600'  },
    { key:'outstation',   label:'Outstation',   count: outstationFiltered.length,   color:'bg-purple-600'},
  ];

  const currentList = tab === 'unclassified' ? unclassifiedFiltered : tab === 'instation' ? instationFiltered : outstationFiltered;

  // NOTE: same delivery-classification color-code as above, left untouched.
  const TYPE_BADGE = {
    instation:    'bg-blue-100 text-blue-700',
    outstation:   'bg-purple-100 text-purple-700',
    unclassified: 'bg-amber-100 text-amber-700',
  };

  if (loading) return <div className="flex justify-center p-12"><Spinner size="lg" /></div>;

  return (
    <div className="p-6 max-w-[1600px]">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Dispatch Overview</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Manage dispatch classification and override delivery types</p>
          {lastUpdated && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
              🔄 Auto-refreshing · Last updated {lastUpdated.toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit', second:'2-digit' })}
            </p>
          )}
        </div>
        <button onClick={load} className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-800">
          🔄 Refresh
        </button>
      </div>

      {msg && <div className="mb-4"><Alert type={msg.type} message={msg.text} onClose={() => setMsg(null)} /></div>}

      {/* View toggle */}
      <div className="flex gap-2 items-center h-14 sticky top-0 z-20 bg-gray-100 dark:bg-gray-900">
        <button onClick={() => setView('classification')}
          className={`px-4 py-2 text-sm font-semibold rounded-lg ${view === 'classification' ? 'bg-brand-700 text-white' : 'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
          Classification
        </button>
        <button onClick={() => setView('rounds')}
          className={`px-4 py-2 text-sm font-semibold rounded-lg ${view === 'rounds' ? 'bg-brand-700 text-white' : 'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
          Dispatch Rounds
        </button>
      </div>

      {/* Date filter */}
      <div className="flex flex-wrap gap-3 items-center mb-5 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3 sticky top-14 z-20">
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-gray-600 dark:text-gray-400">From</label>
          <input type="date" value={dateFrom} disabled={allTime} onChange={e => setDateFrom(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-40" />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-gray-600 dark:text-gray-400">To</label>
          <input type="date" value={dateTo} disabled={allTime} onChange={e => setDateTo(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-40" />
        </div>
        <button onClick={() => { setDateFrom(today()); setDateTo(today()); setAllTime(false); }}
          className="px-3 py-1.5 text-xs font-semibold bg-brand-700/5 text-brand-700 rounded-lg hover:bg-brand-700/10">
          Today
        </button>
        <button onClick={() => setAllTime(v => !v)}
          className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${allTime ? 'bg-green-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200'}`}>
          {allTime ? '✓ All Time' : 'All Time'}
        </button>
      </div>

      {view === 'classification' && (<>
      {unclassified.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
          <p className="text-amber-700 font-semibold">
            ⚠️ {unclassified.length} invoice{unclassified.length !== 1 ? 's' : ''} need classification before dispatch.
            Their pincodes were not found in your instation or outstation lists.
          </p>
          <p className="text-amber-600 text-sm mt-1">
            Go to Settings → update your pincode lists, or manually classify these invoices below.
          </p>
        </div>
      )}

      {/* Tabs — selection state (which classification list is being
          viewed) rebranded; the badge colors above stay tied to the
          actual classification color-code */}
      <div className="flex gap-2 mb-4 border-b border-gray-200 dark:border-gray-700 pb-0">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2.5 text-sm font-semibold rounded-t-lg border-b-2 flex items-center gap-2 transition-colors
              ${tab === t.key ? 'border-brand-700 text-brand-700 bg-brand-700/5' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}
          >
            {t.label}
            <span className={`text-xs text-white px-2 py-0.5 rounded-full ${t.color}`}>{t.count}</span>
          </button>
        ))}
      </div>

      {/* Table */}
      {currentList.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 text-center">
          <p className="text-4xl mb-3">✅</p>
          <p className="text-gray-500 dark:text-gray-400 font-medium">No {tab} invoices in dispatch queue</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                {['Invoice #','Party','City','Pincode','Type','Received','Actions'].map((h,i) => (
                  <th key={i} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currentList.map((inv, i) => (
                <tr key={inv.invoice_id} className="border-t hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-4 py-3 font-bold text-brand-700">{inv.invoice_number}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800 dark:text-gray-100">{inv.party_name}</div>
                    <div className="text-xs text-gray-400 dark:text-gray-500">{formatCurrency(inv.invoice_value)}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{inv.city}</td>
                  <td className="px-4 py-3 font-mono text-gray-600 dark:text-gray-400">{inv.pincode}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${TYPE_BADGE[inv.delivery_type] || 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'}`}>
                      {inv.delivery_type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400 dark:text-gray-500">{formatDate(inv.received_at)}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => { setOverrideInv(inv); setOverrideForm({ delivery_type:'instation', reason:'' }); }}
                      className="text-xs bg-amber-50 text-amber-700 px-2 py-1 rounded hover:bg-amber-100 font-medium"
                    >
                      Override Type
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Override modal */}
      {overrideInv && (
        <Modal title="Override Delivery Type" onClose={() => setOverrideInv(null)}>
          <div className="mb-4 bg-gray-50 dark:bg-gray-900 rounded-lg p-3 text-sm">
            <p className="font-semibold text-gray-700 dark:text-gray-300">{overrideInv.invoice_number}</p>
            <p className="text-gray-500 dark:text-gray-400">{overrideInv.party_name} — {overrideInv.city} — {overrideInv.pincode}</p>
          </div>

          <div className="mb-4">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Reclassify as:</p>
            {/* NOTE: instation/outstation button colors here directly
                represent the classification value being chosen — same
                color-code as the badges elsewhere, left untouched. */}
            <div className="flex gap-3">
              {['instation','outstation'].map(type => (
                <button
                  key={type}
                  onClick={() => setOverrideForm(f => ({ ...f, delivery_type: type }))}
                  className={`flex-1 py-3 rounded-xl text-sm font-semibold border-2 transition-all capitalize
                    ${overrideForm.delivery_type === type
                      ? type === 'instation' ? 'bg-blue-600 text-white border-blue-600' : 'bg-purple-600 text-white border-purple-600'
                      : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400'}`}
                >
                  {type === 'instation' ? '🚚' : '📦'} {type}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-4">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Reason for override *</p>
            <textarea
              value={overrideForm.reason}
              onChange={e => setOverrideForm(f => ({ ...f, reason: e.target.value }))}
              placeholder="e.g. Customer relocated, pincode updated, special arrangement..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
            />
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <p className="text-xs text-amber-700">
              ⚠️ This override is logged in the audit trail with your name, timestamp, and reason.
              Override is only possible before the invoice is picked up by a delivery person.
            </p>
          </div>

          <div className="flex gap-3">
            <button onClick={() => setOverrideInv(null)}
              className="flex-1 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800">
              Cancel
            </button>
            <button onClick={submitOverride} disabled={saving}
              className="flex-1 py-2 bg-amber-600 text-white rounded-lg text-sm font-semibold hover:bg-amber-500 disabled:opacity-60">
              {saving ? 'Saving...' : 'Confirm Override'}
            </button>
          </div>
        </Modal>
      )}
      </>)}

      {view === 'rounds' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {loadingRounds ? (
            <div className="flex justify-center p-12"><Spinner size="lg" /></div>
          ) : rounds.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-4xl mb-3">🛵</p>
              <p className="text-gray-500 dark:text-gray-400 font-medium">No dispatch rounds in this range</p>
            </div>
          ) : (() => {
            const ROUND_COLS = [
              { key: 'round_label', label: 'Round' },
              { key: 'delivery_person_name', label: 'Delivery Person' },
              { key: 'started_at', label: 'Started' },
              { key: 'ended_at', label: 'Ended' },
              { key: 'duration_seconds', label: 'Duration' },
              { key: 'invoice_count', label: 'Invoices' },
              { key: 'delivered_count', label: 'Delivered' },
              { key: 'is_open', label: 'Status' },
            ];
            function sortIcon(col, activeCol, order) {
              if (activeCol !== col) return <span className="text-gray-300 ml-1">↕</span>;
              return <span className="text-brand-500 ml-1">{order === 'asc' ? '↑' : '↓'}</span>;
            }
            function handleRoundSort(col) {
              if (col === roundSortCol) setRoundSortOrder(o => o === 'asc' ? 'desc' : 'asc');
              else { setRoundSortCol(col); setRoundSortOrder(col === 'delivery_person_name' ? 'asc' : 'desc'); }
            }
            function sortRoundsArr(arr) {
              return [...arr].sort((a, b) => {
                let av = a[roundSortCol], bv = b[roundSortCol];
                if (['round_number','duration_seconds','invoice_count','delivered_count'].includes(roundSortCol)) {
                  av = av ?? -1; bv = bv ?? -1;
                } else if (roundSortCol === 'started_at' || roundSortCol === 'ended_at') {
                  av = av ? new Date(av).getTime() : 0; bv = bv ? new Date(bv).getTime() : 0;
                } else if (roundSortCol === 'is_open') {
                  av = av ? 1 : 0; bv = bv ? 1 : 0;
                } else {
                  av = (av || '').toString().toLowerCase(); bv = (bv || '').toString().toLowerCase();
                }
                const cmp = av < bv ? -1 : av > bv ? 1 : 0;
                return roundSortOrder === 'asc' ? cmp : -cmp;
              });
            }

            const INV_COLS = [
              { key: 'invoice_number', label: 'Invoice #' },
              { key: 'party_name', label: 'Party' },
              { key: 'city', label: 'City' },
              { key: 'delivery_type', label: 'Type' },
              { key: 'invoice_value', label: 'Value' },
              { key: 'status', label: 'Status' },
              { key: 'voucher_status', label: 'Outcome' },
            ];
            function handleInvSort(col) {
              if (col === invSortCol) setInvSortOrder(o => o === 'asc' ? 'desc' : 'asc');
              else { setInvSortCol(col); setInvSortOrder('asc'); }
            }
            function sortInvoicesArr(arr) {
              return [...arr].sort((a, b) => {
                let av = a[invSortCol], bv = b[invSortCol];
                if (invSortCol === 'voucher_status') { av = a.confirmation_type || a.voucher_outcome || ''; bv = b.confirmation_type || b.voucher_outcome || ''; }
                if (invSortCol === 'invoice_value') { av = parseFloat(av) || 0; bv = parseFloat(bv) || 0; }
                else { av = (av || '').toString().toLowerCase(); bv = (bv || '').toString().toLowerCase(); }
                const cmp = av < bv ? -1 : av > bv ? 1 : 0;
                return invSortOrder === 'asc' ? cmp : -cmp;
              });
            }

            function toggleDateGroup(dateKey) {
              setCollapsedDateGroups(prev => {
                const next = new Set(prev);
                next.has(dateKey) ? next.delete(dateKey) : next.add(dateKey);
                return next;
              });
            }

            const RoundsTable = ({ list }) => (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    {ROUND_COLS.map(c => (
                      <th key={c.key} onClick={() => handleRoundSort(c.key)}
                        className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:bg-gray-800 select-none">
                        {c.label}{sortIcon(c.key, roundSortCol, roundSortOrder)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortRoundsArr(list).map(r => (
                    <React.Fragment key={r.id}>
                      <tr onClick={() => toggleRound(r)} className="border-t hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer">
                        <td className="px-4 py-3 font-bold text-gray-700 dark:text-gray-300">
                          {expandedRound === r.id ? '▼' : '▶'} {r.round_label || `#${r.round_number}`}
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-800 dark:text-gray-100">{r.delivery_person_name}</td>
                        <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">{formatDate(r.started_at)}</td>
                        <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">{r.ended_at ? formatDate(r.ended_at) : '—'}</td>
                        <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                          {r.duration_seconds != null ? formatDuration(r.duration_seconds) : (r.is_open ? 'In progress' : '—')}
                        </td>
                        <td className="px-4 py-3 font-semibold text-brand-700">{r.invoice_count}</td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{r.delivered_count}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${r.is_open ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                            {r.is_open ? 'On Route' : 'Returned'}
                          </span>
                        </td>
                      </tr>
                      {expandedRound === r.id && (
                        <tr className="border-t bg-gray-50 dark:bg-gray-900">
                          <td colSpan={8} className="px-4 py-4">
                            {loadingInvoices === r.id ? (
                              <div className="flex justify-center py-6"><Spinner /></div>
                            ) : (roundInvoices[r.id] || []).length === 0 ? (
                              <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">No invoices recorded for this round</p>
                            ) : (
                              <table className="w-full text-xs bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                                <thead className="bg-gray-100 dark:bg-gray-800">
                                  <tr>
                                    {INV_COLS.map(c => (
                                      <th key={c.key} onClick={() => handleInvSort(c.key)}
                                        className="text-left px-3 py-2 font-semibold text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-200 select-none">
                                        {c.label}{sortIcon(c.key, invSortCol, invSortOrder)}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {sortInvoicesArr(roundInvoices[r.id]).map(inv => (
                                    <tr key={inv.delivery_id} className="border-t">
                                      <td className="px-3 py-2 font-bold text-brand-700 cursor-pointer hover:underline"
                                          onClick={() => navigate(`/invoices/${inv.invoice_id}`)}>
                                        {inv.invoice_number}
                                      </td>
                                      <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{inv.party_name}</td>
                                      <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{inv.city}</td>
                                      <td className="px-3 py-2 capitalize text-gray-500 dark:text-gray-400">{inv.delivery_type}</td>
                                      <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{formatCurrency(inv.invoice_value)}</td>
                                      <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{(inv.status || '').replace(/_/g, ' ')}</td>
                                      <td className="px-3 py-2 text-gray-500 dark:text-gray-400">
                                        {inv.confirmation_type || inv.voucher_outcome || '—'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            );

            if (!allTime) return <RoundsTable list={rounds} />;

            // All Time: group into collapsible date sections instead of
            // one giant flat list.
            const groups = {};
            for (const r of rounds) {
              const dateKey = r.started_at ? r.started_at.slice(0, 10) : 'Unknown';
              if (!groups[dateKey]) groups[dateKey] = [];
              groups[dateKey].push(r);
            }
            const sortedDateKeys = Object.keys(groups).sort((a, b) => b.localeCompare(a));

            return (
              <div>
                {sortedDateKeys.map(dateKey => {
                  const isCollapsed = collapsedDateGroups.has(dateKey);
                  return (
                    <div key={dateKey}>
                      <button onClick={() => toggleDateGroup(dateKey)}
                        className="w-full text-left px-4 py-3 bg-gray-100 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex items-center gap-3 hover:bg-gray-200">
                        <span className={`text-xs transition-transform ${isCollapsed ? '-rotate-90' : ''}`}>▼</span>
                        <span className="font-bold text-gray-700 dark:text-gray-300 text-sm">{formatDate(dateKey)}</span>
                        <span className="text-xs text-gray-400 dark:text-gray-500 font-semibold">({groups[dateKey].length} round{groups[dateKey].length !== 1 ? 's' : ''})</span>
                      </button>
                      {!isCollapsed && <RoundsTable list={groups[dateKey]} />}
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
